/**
 * MAYOR'S DESK — the morning espresso.
 *
 * One screen a mayor can read in thirty seconds and forward to his group: the
 * single thing that needs a decision, the situations behind it, the ranked next
 * steps, and the morning's headlines with plain-language versions of anything
 * that arrived in another language.
 *
 * Pure and dependency-free so every rule here is pinned by unit tests:
 *
 *  · MOST IMPORTANT FIRST — the lead compartment is the highest-severity item
 *    on the action board, and the situation compartments are ordered by their
 *    own severity, so the worst thing is always the biggest thing on screen.
 *  · THE BENTO IS A COMPOSITION, NOT A GRID — compartments differ in size
 *    because their contents differ: the lead earns two rows and seven columns
 *    because it carries a headline, its evidence and a next step; the morning
 *    headlines earn eight columns because each entry shows a plain line AND
 *    the original line. `placeBriefAreas` is the single source of that
 *    geometry, and a unit test asserts the layout never leaves a hole.
 *  · A DEAD FEED SAYS "NO DATA" — never "all clear". A signal whose feed is
 *    down is rendered as an unknown-level compartment with its source named.
 *  · THE MODEL TRANSLATES; IT NEVER DECIDES — the plain headline is a
 *    convenience over the original, and the original is always present beside
 *    it. A missing translation leaves `plain` null; nothing is invented.
 */

import type { HeadlineLang } from "@yala/shared";
import type { ActionItem, ActionSeverity, Bilingual } from "./actionBoard";

export type BriefLevel = "critical" | "warning" | "watch" | "good" | "unknown";

/** The brief grid is twelve columns wide on desktop; one on a phone. */
export const BRIEF_COLUMNS = 12;

/** Where a compartment sits on the brief grid. 1-based, like CSS. */
export interface BriefArea {
  col: number;
  span: number;
  row: number;
  rows: number;
}

/** One situation on the board: water, security, weather, air, incidents. */
export interface BriefSignal {
  key: string;
  level: BriefLevel;
  title: Bilingual;
  /** The one line that matters. */
  value: Bilingual;
  /** Supporting numbers, if there are any worth the line. */
  facts?: Bilingual;
  /** Where the numbers came from. */
  source: string;
  /** Age of the reading in minutes; null when it is not a timed reading. */
  ageMinutes: number | null;
}

/** A headline in the morning's news, with its plain-language version. */
export interface BriefNewsItem {
  id: string;
  /** The original headline. Always shown — it is the evidence. */
  title: string;
  /** The language the original was written in. */
  lang: HeadlineLang;
  /** Machine-written plain headline; null when no translation is available. */
  plain: string | null;
  source: string;
  sourceUrl: string;
  publishedAt: string | null;
  /** Mayor-action codes from the feed (EM, SEC, FL, …). */
  tags?: string[];
}

export interface MayorBrief {
  /** The worst level anywhere in the brief. */
  level: BriefLevel;
  /** One line: the state of the city this morning. */
  summary: Bilingual;
  /** The single most important thing, with the step to take. */
  lead: ActionItem | null;
  /** Ranked next steps. */
  actions: ActionItem[];
  /** Situations, most severe first. */
  signals: BriefSignal[];
  /** The morning's headlines. */
  news: BriefNewsItem[];
  /** Grid placement for every compartment. */
  areas: Record<string, BriefArea>;
  /** True when a headline was machine-translated for this brief. */
  translated: boolean;
}

/**
 * How many ranked steps the brief carries.
 *
 * An ESPRESSO, not a report: three decisions and four headlines. The full
 * lists are one click away — the ranked board in the rail, the whole feed in
 * the news desk — so nothing is hidden, it is simply not what the mayor reads
 * first. These numbers are what make the brief fit one screen.
 */
export const BRIEF_ACTION_LIMIT = 3;
/** How many headlines the brief carries. */
export const BRIEF_NEWS_LIMIT = 4;

/** Rank order for brief levels — lower sorts first. */
const LEVEL_RANK: Record<BriefLevel, number> = {
  critical: 0,
  warning: 1,
  watch: 2,
  good: 3,
  unknown: 4,
};

/** Rank order for action-board severities — lower sorts first. */
const ACTION_RANK: Record<ActionSeverity, number> = {
  critical: 0,
  warning: 1,
  watch: 2,
  blindspot: 3,
};

/** An action-board severity is a brief level; a blind spot is "unknown". */
export function levelFromSeverity(severity: ActionSeverity): BriefLevel {
  switch (severity) {
    case "critical": return "critical";
    case "warning": return "warning";
    case "watch": return "watch";
    case "blindspot": return "unknown";
  }
}

/** The worst level in a list. An empty list is "unknown", never "good". */
export function worstLevel(levels: BriefLevel[]): BriefLevel {
  if (levels.length === 0) return "unknown";
  return levels.reduce((worst, l) => (LEVEL_RANK[l] < LEVEL_RANK[worst] ? l : worst));
}

/**
 * Split `total` columns into `parts` INTEGER spans that sum to `total`.
 *
 * The grid only accepts whole columns — `span 2.4` is not a number the
 * browser will place — so a row of five compartments takes 3+3+2+2+2 rather
 * than five 2.4s.
 */
export function splitColumns(total: number, parts: number): number[] {
  if (parts <= 0) return [];
  const base = Math.floor(total / parts);
  const extra = total - base * parts;
  return Array.from({ length: parts }, (_, i) => base + (i < extra ? 1 : 0));
}

/**
 * The bento geometry.
 *
 * The lead takes seven columns and two rows on the left. The MOST SEVERE
 * situation takes the five columns beside it and spans the same two rows, so
 * the band is sized by the lead's own content instead of by two stacked
 * panels. The remaining situations share the full width of the third row.
 * The ranked steps and the headlines close the brief.
 *
 * The layout always fills a complete rectangle — every row edge to edge, no
 * hole in it whatever the feed mix. A hole would be unexplained space, which
 * is exactly what this app is not allowed to ship.
 */
export function placeBriefAreas(signalCount: number): {
  lead: BriefArea;
  signals: BriefArea[];
  actions: BriefArea;
  news: BriefArea;
} {
  const signals: BriefArea[] = [];
  let lead: BriefArea;
  let bodyRow = 4;

  if (signalCount === 0) {
    lead = { col: 1, span: BRIEF_COLUMNS, row: 1, rows: 3 };
  } else if (signalCount === 1) {
    lead = { col: 1, span: 7, row: 1, rows: 3 };
    signals.push({ col: 8, span: 5, row: 1, rows: 3 });
  } else {
    lead = { col: 1, span: 7, row: 1, rows: 2 };
    // The worst situation sits beside the lead, spanning both of its rows.
    signals.push({ col: 8, span: 5, row: 1, rows: 2 });

    // The rest share the rows below, side by side, each row filling the grid
    // exactly. Six is the most that fit legibly in one row.
    const MAX_PER_ROW = 6;
    const rest = signalCount - 1;
    let placed = 0;
    let row = 3;
    while (placed < rest) {
      const inRow = Math.min(MAX_PER_ROW, rest - placed);
      const spans = splitColumns(BRIEF_COLUMNS, inRow);
      let col = 1;
      for (const span of spans) {
        signals.push({ col, span, row, rows: 1 });
        col += span;
        placed++;
      }
      row++;
    }
    bodyRow = row;
  }

  return {
    lead,
    signals,
    actions: { col: 1, span: 4, row: bodyRow, rows: 2 },
    news: { col: 5, span: 8, row: bodyRow, rows: 2 },
  };
}

export interface MayorBriefInput {
  /** Ranked decisions from the action board (already severity-ordered). */
  actions: ActionItem[];
  /** The situations. The engine orders them by severity, not by this order. */
  signals: BriefSignal[];
  /** Headlines, already relevance-sorted by the API. */
  news: BriefNewsItem[];
  /** Machine translations keyed by headline id. */
  translations?: Record<string, string>;
  /** How many headlines to carry. */
  newsLimit?: number;
  /** How many ranked steps to carry. */
  actionLimit?: number;
}

/**
 * Assemble the brief.
 *
 * The lead is the highest-severity ACTION — the thing that needs a decision —
 * because that is what a mayor opens the desk for. The situations are ordered
 * by their own severity so the worst situation is always the largest one
 * beside it.
 */
export function buildMayorBrief(input: MayorBriefInput): MayorBrief {
  const actionLimit = input.actionLimit ?? BRIEF_ACTION_LIMIT;
  const newsLimit = input.newsLimit ?? BRIEF_NEWS_LIMIT;

  // Rank the decisions here, not by trusting the caller: "most important
  // first" is the brief's contract, so it must hold whoever feeds it.
  const ranked = [...input.actions].sort(
    (a, b) => ACTION_RANK[a.severity] - ACTION_RANK[b.severity],
  );
  const actionable = ranked.filter((a) => a.severity !== "blindspot");
  const blindspots = ranked.filter((a) => a.severity === "blindspot");
  const lead = actionable[0] ?? null;

  const signals = [...input.signals].sort(
    (a, b) => LEVEL_RANK[a.level] - LEVEL_RANK[b.level],
  );

  const translations = input.translations ?? {};
  const news = input.news.slice(0, newsLimit).map((item) => {
    const plain = translations[item.id];
    // Only a translation we actually hold for THIS headline is used. A missing
    // one leaves the original alone — the brief never invents a headline.
    return { ...item, plain: plain && plain.trim() ? plain.trim() : null };
  });

  const actions = [...actionable, ...blindspots].slice(0, actionLimit);
  const areas = placeBriefAreas(signals.length);

  const level = worstLevel([
    lead ? levelFromSeverity(lead.severity) : "unknown",
    ...signals.map((s) => s.level),
  ]);

  const summary = briefSummary({
    level,
    lead,
    blindspotCount: blindspots.length,
    signalCount: signals.length,
  });

  return {
    level,
    summary,
    lead,
    actions,
    signals,
    news,
    areas: {
      lead: areas.lead,
      ...Object.fromEntries(signals.map((s, i) => [s.key, areas.signals[i]])),
      actions: areas.actions,
      news: areas.news,
    },
    translated: news.some((n) => n.plain !== null),
  };
}

const LEVEL_LABEL: Record<BriefLevel, { en: string; th: string }> = {
  critical: { en: "CRITICAL", th: "วิกฤต" },
  warning: { en: "PREPARE", th: "เตรียมพร้อม" },
  watch: { en: "WATCH", th: "เฝ้าระวัง" },
  good: { en: "NORMAL", th: "ปกติ" },
  unknown: { en: "NO DATA", th: "ไม่มีข้อมูล" },
};

export function briefLevelLabel(level: BriefLevel): Bilingual {
  return LEVEL_LABEL[level];
}

/**
 * One line: the state of the city this morning.
 *
 * "Nothing is over threshold" is only honest when we can actually see. If
 * signals are not reporting, the line says the status is unknown instead.
 */
export function briefSummary(i: {
  level: BriefLevel;
  lead: ActionItem | null;
  blindspotCount: number;
  signalCount: number;
}): Bilingual {
  const blind = i.blindspotCount;
  if (i.lead) {
    return {
      en: blind > 0
        ? `${i.lead.title.en} — and ${blind} signal${blind > 1 ? "s" : ""} not reporting`
        : i.lead.title.en,
      th: blind > 0
        ? `${i.lead.title.th} — และมีอีก ${blind} สัญญาณที่ไม่ส่งข้อมูล`
        : i.lead.title.th,
    };
  }
  if (blind > 0) {
    return {
      en: `STATUS UNKNOWN — ${blind} signal${blind > 1 ? "s" : ""} not reporting`,
      th: `ไม่ทราบสถานะ — มี ${blind} สัญญาณที่ไม่ส่งข้อมูล`,
    };
  }
  return {
    en: "Nothing is over threshold — all monitored signals are reporting",
    th: "ไม่มีรายการเกินเกณฑ์ — สัญญาณที่ติดตามอยู่ส่งข้อมูลครบ",
  };
}

/**
 * Plain text for LINE and for print.
 *
 * No markdown — LINE shows it literally. Every line keeps its source, and a
 * machine-translated headline is marked as one, with the original underneath.
 */
export function briefText(brief: MayorBrief, lang: "en" | "th", now = new Date()): string {
  const th = lang === "th";
  const pick = (b: Bilingual) => (th ? b.th : b.en);
  const stamp = now.toLocaleString(th ? "th-TH" : "en-GB", {
    timeZone: "Asia/Bangkok",
    dateStyle: "long",
    timeStyle: "short",
  });
  const marker = (level: BriefLevel) => `[${pick(briefLevelLabel(level))}]`;

  const lines: string[] = [];
  lines.push(
    th
      ? `สรุปโต๊ะนายกเทศมนครยะลา — ${stamp}`
      : `Yala Mayor's Desk — ${stamp}`,
  );
  lines.push(pick(brief.summary));
  lines.push("");

  if (brief.lead) {
    lines.push(
      `${marker(levelFromSeverity(brief.lead.severity))} ${pick(brief.lead.title)}`,
    );
    lines.push(`  ${th ? "หลักฐาน" : "Evidence"}: ${brief.lead.evidence}`);
    lines.push(`  ${th ? "ขั้นตอนถัดไป" : "Next step"}: ${pick(brief.lead.action)}`);
    lines.push("");
  }

  if (brief.signals.length > 0) {
    lines.push(th ? "สถานการณ์" : "SITUATION");
    for (const s of brief.signals) {
      lines.push(`${marker(s.level)} ${pick(s.title)}: ${pick(s.value)}`);
      if (s.facts) lines.push(`  ${pick(s.facts)}`);
      lines.push(`  ${th ? "ที่มา" : "Source"}: ${s.source}`);
    }
    lines.push("");
  }

  if (brief.actions.length > 0) {
    lines.push(th ? "ประเด็นที่ต้องตัดสินใจ" : "POINTS TO DECIDE");
    brief.actions.forEach((a, i) => {
      lines.push(`${i + 1}. [${pick(briefLevelLabel(levelFromSeverity(a.severity)))}] ${pick(a.title)}`);
      lines.push(`   ${pick(a.action)}`);
    });
    lines.push("");
  }

  if (brief.news.length > 0) {
    lines.push(th ? "ข่าวเช้า" : "MORNING NEWS");
    for (const n of brief.news) {
      if (n.plain) {
        lines.push(`${th ? "แปล (AI)" : "Plain (AI)"}: ${n.plain}`);
        lines.push(`${th ? "ต้นฉบับ" : "Original"}: ${n.title}`);
      } else {
        lines.push(n.title);
      }
      lines.push(`  ${th ? "ที่มา" : "Source"}: ${n.source}`);
    }
  }

  return lines.join("\n").trimEnd();
}
